// @cogo/template — fidelity gates (the contract's teeth)
//
// PURE-ish leaf module: no node:fs, no network, no Date.now/Math.random.
// `verifyStructureHash`/`buildTemplate` are async only because they call
// through to canonical.ts's `computeStructureHash` (WebCrypto SHA-256).
//
// Four gates:
//  - assertRoundTrip: pkg must reproduce sourceBundle byte-for-byte when
//    instantiated with every slot's own sourceValue — an authoring
//    self-check ("does this package's declared sourceValue set actually
//    describe the bundle it was cut from?").
//  - diffDeclaredOnly: a source/instance pair may differ ONLY at declared
//    slot addresses (+ the design file, when rebinding is "allowed") — any
//    other difference is an unauthorized template mutation (hand edit,
//    generator drift, etc).
//  - verifyStructureHash: an instance's masked structure must hash to the
//    exact value the package was authored against (pkg.source.structureHash).
//  - buildTemplate: the sole authoring entry point. Composes
//    verificationEvidence check -> validateSlotDecls -> computeStructureHash
//    -> self assertRoundTrip, and either returns a publishable
//    TemplatePackage or the typed failures that blocked it.
//
// canonical.ts's `maskBundle`/`computeStructureHash` are throw-based on
// purpose (see that module's header): every call site here catches the
// TypeError and converts it to a TypedFailure so no gate ever lets a raw
// throw escape to a caller (LLM retry loop / CLI / CI gate) that expects a
// structured, enumerable failure list.

import type { AppBundle, Json, SlotDecl, TemplatePackage, TypedFailure } from './types'
import { canonicalJson, computeStructureHash, maskBundle } from './canonical'
import { validateSlotDecls } from './validate-slots'
import { instantiate } from './instantiate'

/**
 * Converts a caught `maskBundle`/`computeStructureHash` throw into a typed
 * failure. Both functions only ever throw for a declared slot address that
 * doesn't resolve, or a missing design-system file when rebinding is
 * "allowed" — both are, semantically, "a declared address doesn't hold up"
 * cases. The closed failure-code set (§7) has no separate code for the
 * design-file case, so this deliberately reuses `slot-address-missing` for
 * both, per the Task 6 brief.
 */
function caughtAsFailure(err: unknown): TypedFailure {
  return { code: 'slot-address-missing', message: err instanceof Error ? err.message : String(err) }
}

function fileAt(bundle: AppBundle, path: string): Json | undefined {
  return bundle.files[path]
}

function allFileKeys(a: AppBundle, b: AppBundle): string[] {
  return Array.from(new Set([...Object.keys(a.files), ...Object.keys(b.files)])).sort()
}

/** `true` when the two bundles' files at `path` are canonically identical (missing on either side counts as a difference). */
function filesDiffer(a: AppBundle, b: AppBundle, path: string): boolean {
  const left = fileAt(a, path)
  const right = fileAt(b, path)
  if (left === undefined || right === undefined) return left !== right
  return canonicalJson(left) !== canonicalJson(right)
}

// ---------------------------------------------------------------------------
// Gate 1: assertRoundTrip
// ---------------------------------------------------------------------------

/**
 * Gate 1: instantiates `pkg` against `sourceBundle` using every slot's own
 * `sourceValue`, and requires the result to be canonically identical to
 * `sourceBundle`, file by file. A package whose declared sourceValues don't
 * reproduce the bundle it claims to be authored from is broken at the root
 * — every other guarantee the package makes is downstream of this one.
 */
export async function assertRoundTrip(pkg: TemplatePackage, sourceBundle: AppBundle): Promise<TypedFailure[]> {
  const slotValues: Record<string, Json> = {}
  for (const slot of pkg.slots) {
    slotValues[slot.slotId] = slot.sourceValue
  }

  const result = instantiate({ pkg, sourceBundle, slotValues })
  if (!result.ok) {
    return result.failures.map((f) => ({
      code: 'template-roundtrip-broken',
      message: `instantiating with every slot's own sourceValue failed: ${f.message}`,
      slotId: f.slotId,
    }))
  }

  const failures: TypedFailure[] = []
  for (const path of allFileKeys(sourceBundle, result.bundle)) {
    if (filesDiffer(sourceBundle, result.bundle, path)) {
      failures.push({
        code: 'template-roundtrip-broken',
        message: `file "${path}" does not round-trip when instantiated with every slot's own sourceValue`,
        path,
      })
    }
  }
  return failures
}

// ---------------------------------------------------------------------------
// Gate 2: diffDeclaredOnly
// ---------------------------------------------------------------------------

/**
 * Gate 2: masks BOTH `sourceBundle` and `instance` with the same
 * slots/design binding (see canonical.ts's `maskBundle`), then requires the
 * two masked bundles to be canonically identical, file by file. Masking
 * erases exactly the declared slot values (and the design file, when
 * rebinding is "allowed") — so any residual difference between the masked
 * bundles is a structural mutation outside the declared contract. Reports
 * only the first differing file path.
 */
export function diffDeclaredOnly(pkg: TemplatePackage, sourceBundle: AppBundle, instance: AppBundle): TypedFailure[] {
  let maskedSource: AppBundle
  let maskedInstance: AppBundle
  try {
    maskedSource = maskBundle(sourceBundle, pkg.slots, pkg.designBinding)
    maskedInstance = maskBundle(instance, pkg.slots, pkg.designBinding)
  } catch (err) {
    return [caughtAsFailure(err)]
  }

  for (const path of allFileKeys(maskedSource, maskedInstance)) {
    if (filesDiffer(maskedSource, maskedInstance, path)) {
      return [
        {
          code: 'template-nonslot-divergence',
          message: `file "${path}" differs outside declared slot addresses`,
          path,
        },
      ]
    }
  }
  return []
}

// ---------------------------------------------------------------------------
// Gate 3: verifyStructureHash
// ---------------------------------------------------------------------------

/**
 * Gate 3: recomputes `instance`'s masked structure hash and requires it to
 * equal `pkg.source.structureHash` exactly. Slot-value edits (the whole
 * point of instantiation) don't move the hash — only a structural change
 * does, so this is the cheap, single-value check for "is this instance
 * still shaped like the template it claims to come from."
 */
export async function verifyStructureHash(pkg: TemplatePackage, instance: AppBundle): Promise<TypedFailure[]> {
  let hash: string
  try {
    hash = await computeStructureHash(instance, pkg.slots, pkg.designBinding)
  } catch (err) {
    return [caughtAsFailure(err)]
  }
  if (hash !== pkg.source.structureHash) {
    return [
      {
        code: 'template-structure-hash-mismatch',
        message: `structureHash mismatch: expected "${pkg.source.structureHash}", computed "${hash}"`,
      },
    ]
  }
  return []
}

// ---------------------------------------------------------------------------
// composite build gate: buildTemplate (evidence -> decls -> hash -> self round-trip)
// ---------------------------------------------------------------------------

export interface BuildTemplateInput {
  bundle: AppBundle
  slots: SlotDecl[]
  templateId: string
  version: string
  kind: TemplatePackage['kind']
  projectRef: string
  verificationEvidence: string[]
  designRebinding: 'allowed' | 'pinned'
}

export type BuildTemplateResult = { ok: true; pkg: TemplatePackage } | { ok: false; failures: TypedFailure[] }

/**
 * The sole authoring entry point: `verificationEvidence.length >= 1` (else
 * `template-source-unverified`) -> `validateSlotDecls` (address resolution +
 * forbidden-position checks against `input.bundle`) -> `computeStructureHash`
 * -> self `assertRoundTrip` (the package must reproduce its own source
 * bundle) -> package out. Any stage's failures short-circuit the rest.
 */
export async function buildTemplate(input: BuildTemplateInput): Promise<BuildTemplateResult> {
  if (input.verificationEvidence.length < 1) {
    return {
      ok: false,
      failures: [
        {
          code: 'template-source-unverified',
          message: 'verificationEvidence must contain at least one entry before a template can be authored',
        },
      ],
    }
  }

  const designBinding: TemplatePackage['designBinding'] = { file: 'design-system.json', rebinding: input.designRebinding }
  const draft: TemplatePackage = {
    schemaVersion: 'cogo.template.v1',
    templateId: input.templateId,
    version: input.version,
    kind: input.kind,
    source: { projectRef: input.projectRef, structureHash: '', verificationEvidence: input.verificationEvidence },
    designBinding,
    slots: input.slots,
  }

  const declFailures = validateSlotDecls(draft, input.bundle)
  if (declFailures.length > 0) {
    return { ok: false, failures: declFailures }
  }

  let structureHash: string
  try {
    structureHash = await computeStructureHash(input.bundle, input.slots, designBinding)
  } catch (err) {
    return { ok: false, failures: [caughtAsFailure(err)] }
  }

  const pkg: TemplatePackage = { ...draft, source: { ...draft.source, structureHash } }

  const roundTripFailures = await assertRoundTrip(pkg, input.bundle)
  if (roundTripFailures.length > 0) {
    return { ok: false, failures: roundTripFailures }
  }

  return { ok: true, pkg }
}
